import { ClientTagKey } from "intake_shared/src/client_tags";
import {
    ConsentForm,
    ClientStatus,
    IAppointmentChecks,
    IClient,
    IClientBooking,
    ISessionAttendeesHistory,
    ISubmitCalculateClientFee,
    SortConfigProps,
    UpdatedFrom,
} from "intake_shared/src/types";
import db, { dbv9 } from "../utils/firestore";
import { getPhoneNumberSearchVariations, getStringVariations } from "../utils/utils";
import { Collection } from "intake_shared/src/enum";
import { convertStoreDocToClient } from "./util";
import {
    collection,
    doc,
    getDoc,
    getDocs,
    FieldPath,
    query,
    runTransaction,
    serverTimestamp,
    Timestamp,
    where,
    limit,
} from "firebase/firestore";
import axios from "../utils/axios";
import {
    getClientWithoutConsentReminderMetadata,
    getUpdatedHeldConsentCheck,
} from "../utils/consent_form";
import { getFeeRevision } from "./feeRevision";

const clientCollection = collection(db, Collection.CLIENTS);
function normalizeEmail(email: string): string {
    return email.toLowerCase().trim();
}

export async function saveClientData(
    client: IClient,
    last_updated_by: string,
    updateStatusHistory = false,
    saveFeeChanges = false
) {
    const clientData = getClientWithoutConsentReminderMetadata(client);
    delete clientData.old_ids;
    delete clientData.tags;
    delete clientData.active_tag_keys;
    delete clientData.mantle_session_source_revisions;
    delete clientData.mantle_supervisor_intake_source_revisions;
    delete clientData.mantle_fee_source_revision;
    delete clientData.loaded_fee_revision;
    if (!saveFeeChanges) {
        delete clientData[ClientStatus.FEE_SETTING];
        delete clientData.clinician_fees;
        delete clientData.fees_update_details;
        delete clientData.fees_updated_by;
    }
    const client_doc = {
        ...clientData,
        email: normalizeEmail(client.email),
        inquiry_method: client.inquiry_method || "",
        date_updated: Timestamp.fromDate(new Date()),
        last_updated_by,
        updated_from: UpdatedFrom.INTAKE,
    };
    if (updateStatusHistory) {
        client.status_history[client.status] = new Date();
    }
    const clientRef = doc(dbv9, Collection.CLIENTS, client_doc.id);
    let committedFeeRevision: string | undefined;
    await runTransaction(dbv9, async (transaction) => {
        if (saveFeeChanges) {
            const persistedSnapshot = await transaction.get(clientRef);
            if (!persistedSnapshot.exists()) {
                throw new Error("Client not found.");
            }
            const persistedClient = persistedSnapshot.data() as IClient;
            const persistedRevision = getFeeRevision(
                persistedClient.fees_update_details?.updated_at
            );
            const submittedRevision = getFeeRevision(client.loaded_fee_revision);
            if (persistedRevision !== submittedRevision) {
                throw new Error(
                    "Client fees changed after this chart was loaded. Refresh and try again."
                );
            }
            const updatedAt = new Date();
            client_doc.fees_update_details = {
                ...(client.fees_update_details ?? {}),
                updated_by: last_updated_by,
                updated_at: updatedAt,
            };
            committedFeeRevision = updatedAt.toISOString();
        }
        transaction.set(clientRef, client_doc, { merge: true });
    });
    if (committedFeeRevision) {
        client.loaded_fee_revision = committedFeeRevision;
        client.fees_update_details = client_doc.fees_update_details;
    }
}

export async function updateHeldAppointmentConsentCheck(
    clientId: string,
    appointmentId: string,
    consentForm: ConsentForm | undefined,
    isGoodFormOnFile: boolean,
    lastUpdatedBy: string
): Promise<IAppointmentChecks> {
    const clientRef = doc(dbv9, Collection.CLIENTS, clientId);

    return runTransaction(dbv9, async (transaction) => {
        const clientSnapshot = await transaction.get(clientRef);
        if (!clientSnapshot.exists()) {
            throw new Error("Client not found.");
        }

        const appointmentChecks = clientSnapshot.data().appointment_checks as
            | IAppointmentChecks
            | undefined;
        const updatedAppointmentCheck = getUpdatedHeldConsentCheck(
            appointmentChecks?.[appointmentId],
            consentForm,
            isGoodFormOnFile
        );

        transaction.update(
            clientRef,
            new FieldPath("appointment_checks", appointmentId),
            updatedAppointmentCheck,
            "date_updated",
            serverTimestamp(),
            "last_updated_by",
            lastUpdatedBy,
            "updated_from",
            UpdatedFrom.INTAKE
        );

        return {
            ...appointmentChecks,
            [appointmentId]: updatedAppointmentCheck,
        };
    });
}

export async function getClientById(id: string): Promise<IClient | undefined> {
    const client_doc = await getDoc(doc(clientCollection, id));
    if (client_doc.exists()) {
        return convertStoreDocToClient(client_doc);
    }
    return;
}

export async function createNewClient(client: IClient, createdBy: string) {
    const newClient = {
        ...client,
        email: normalizeEmail(client.email),
        created_by: createdBy,
        date_updated: new Date(),
        updated_from: UpdatedFrom.INTAKE,
        last_updated_by: createdBy,
    };
    delete newClient.old_ids;
    delete newClient.tags;
    delete newClient.active_tag_keys;

    await db
        .collection(Collection.CLIENTS)
        .doc(client.id)
        .set(newClient, { merge: true });
}

export async function calculateClientFees(data: ISubmitCalculateClientFee) {
    const token = process.env.REACT_APP_CALCULATE_FEE_TOKEN;
    const response = await axios.post(`/tools/calculate-fees`, data, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data;
}

export async function findClientsByField(
    field: keyof IClient,
    value: any,
    _limit: number = 50,
    status?: ClientStatus
): Promise<IClient[]> {
    const q = query(
        clientCollection,
        where(field, "==", value),
        where("status", "==", status),
        limit(_limit)
    );

    const raw_data = (await getDocs(q)).docs;
    const clients: IClient[] = raw_data.map(convertStoreDocToClient);
    return clients;
}

export async function getAllAssignedClientsByEmail(
    field1: keyof IClient,
    values1: any[],
    field2: keyof IClient,
    values2: any[],
    status: string,
    page: number = 1,
    pageSize: number = 50,
    tagKeys: ClientTagKey[] = []
): Promise<{ clients: IClient[]; assigneeCounts: Record<string, number> }> {
    const perQuery = Math.max(1, Math.floor(30 / Math.max(tagKeys.length, 1)));
    const snapshots = await Promise.all(
        [[field1, values1] as const, [field2, values2] as const].flatMap(
            ([field, values]) => {
                const queries: Array<
                    ReturnType<ReturnType<typeof db.collection>["get"]>
                > = [];
                for (let start = 0; start < values.length; start += perQuery) {
                    let query = db
                        .collection(Collection.CLIENTS)
                        .where(field, "in", values.slice(start, start + perQuery))
                        .where("status", "==", status)
                        .orderBy("date_updated", "asc");
                    if (tagKeys.length)
                        query = query.where(
                            "active_tag_keys",
                            "array-contains-any",
                            tagKeys
                        );
                    queries.push(query.get());
                }
                return queries;
            }
        )
    );
    const matchingClients = snapshots.flatMap((snapshot) =>
        snapshot.docs.map(convertStoreDocToClient)
    );

    const mergedMap = new Map<string, IClient>();
    matchingClients.forEach((client) => mergedMap.set(client.id, client));
    const mergedClients = Array.from(mergedMap.values()).sort(
        (a, b) =>
            new Date(String(a.date_updated)).getTime() -
            new Date(String(b.date_updated)).getTime()
    );

    const assigneeCounts: Record<string, number> = {};
    mergedClients.forEach((client) => {
        if (client.assigned_to)
            assigneeCounts[client.assigned_to] =
                (assigneeCounts[client.assigned_to] || 0) + 1;
        if (client.secondary_assignee)
            assigneeCounts[client.secondary_assignee] =
                (assigneeCounts[client.secondary_assignee] || 0) + 1;
    });

    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return { clients: mergedClients.slice(startIndex, endIndex), assigneeCounts };
}

export async function findClientsByFirstAndLastName({
    firstName,
    lastName,
    phoneNo,
    dateOfBirth,
    tagKeys = [],
}: {
    tagKeys?: ClientTagKey[];
    firstName?: string;
    lastName?: string;
    phoneNo?: string;
    dateOfBirth?: Date | null;
}): Promise<IClient[]> {
    let db_query: any = await db.collection(Collection.CLIENTS);
    if (tagKeys.length)
        db_query = db_query.where("active_tag_keys", "array-contains-any", tagKeys);

    if (firstName) {
        db_query = db_query.where(
            "first_name",
            "in",
            getStringVariations(firstName)
        );
    } else if (lastName) {
        db_query = db_query.where("last_name", "in", getStringVariations(lastName));
    } else if (phoneNo) {
        db_query = db_query.where(
            "phone_no",
            "in",
            getPhoneNumberSearchVariations(phoneNo)
        );
    } else if (dateOfBirth) {
        // This query is to include dates greater than or equal to the user-provided date of birth and
        // less than the day after. So, it effectively covers the entire day of the user's date of birth, this is because
        // the time in the firestore time stamp and the time in the provided date will be different
        // and we really just want to compare the date.

        db_query = db_query
            .where("date_of_birth", ">=", dateOfBirth)
            .where(
                "date_of_birth",
                "<",
                new Date(dateOfBirth.getTime() + 24 * 60 * 60 * 1000)
            );
    }
    const raw_data = (await db_query.get()).docs;
    const clients: IClient[] = raw_data.map(convertStoreDocToClient);
    return clients;
}

export async function fetchClientsByEmails(
    emails: string[],
    tagKeys: ClientTagKey[] = []
): Promise<IClient[]> {
    const normalized = Array.from(
        new Set(emails.map((e) => e.trim().toLowerCase()).filter(Boolean))
    );
    if (normalized.length === 0) return [];

    let clients: IClient[] = [];
    const perQuery = Math.min(
        10,
        Math.max(1, Math.floor(30 / Math.max(tagKeys.length, 1)))
    );
    for (let i = 0; i < normalized.length; i += perQuery) {
        const chunk = normalized.slice(i, i + perQuery);
        let query = db.collection(Collection.CLIENTS).where("email", "in", chunk);
        if (tagKeys.length)
            query = query.where("active_tag_keys", "array-contains-any", tagKeys);
        const raw_data = (await query.get()).docs;
        clients = clients.concat(raw_data.map(convertStoreDocToClient));
    }
    return clients;
}

export async function deleteClient(clientId: string) {
    await db.collection(Collection.CLIENTS).doc(clientId).delete();
}

export async function fetchClientsByIds(ids: string[]): Promise<IClient[]> {
    let clients: IClient[] = [];
    while (ids.length > 0) {
        const id_set = ids.splice(0, 10);
        const raw_data = (
            await (
                await db.collection(Collection.CLIENTS).where("id", "in", id_set)
            ).get()
        ).docs;
        clients = clients.concat(raw_data.map(convertStoreDocToClient));
    }
    return clients;
}

export async function fetchAllClientsData(lastDoc?: IClient): Promise<IClient[]> {
    let next_records_query = db
        .collection(Collection.CLIENTS)
        .orderBy("date_updated", "asc")
        .limit(50);
    if (lastDoc) {
        next_records_query.startAfter(lastDoc.id);
    }
    const raw_data = (await next_records_query.get()).docs;
    const clients: IClient[] = raw_data.map(convertStoreDocToClient);
    return clients;
}

export async function fetchAllClientsByStatus(
    statuses: ClientStatus[],
    sortBy: "asc" | "desc" = "asc",
    sortConfig: SortConfigProps,
    page: number = 1,
    pageSize: number = 50,
    tagKeys: ClientTagKey[] = []
): Promise<{ clients: IClient[]; assigneeCounts: Record<string, number> }> {
    const { key, direction } = sortConfig;
    const sortDirection = direction ? direction : sortBy;
    let client_collection_query = db
        .collection(Collection.CLIENTS)
        .orderBy(key, sortDirection)
        .where("status", "in", statuses);

    if (tagKeys.length)
        client_collection_query = client_collection_query.where(
            "active_tag_keys",
            "array-contains-any",
            tagKeys
        );
    const querySnapshot = await client_collection_query.get();

    const assigneeCounts: Record<string, number> = {};
    querySnapshot.docs.forEach((doc) => {
        const { assigned_to, secondary_assignee } = doc.data();
        if (assigned_to)
            assigneeCounts[assigned_to] = (assigneeCounts[assigned_to] || 0) + 1;
        if (secondary_assignee)
            assigneeCounts[secondary_assignee] =
                (assigneeCounts[secondary_assignee] || 0) + 1;
    });

    // The full scan above already holds every page in query order, so slice the
    // requested page from it instead of re-reading those documents.
    const startIndex = (page - 1) * pageSize;
    const clients: IClient[] = querySnapshot.docs
        .slice(startIndex, startIndex + pageSize)
        .map(convertStoreDocToClient);
    return { clients, assigneeCounts };
}

export async function updateClientData({
    client_id,
    changes = {},
    confirmed_bookings,
    held_bookings,
    last_updated_by,
}: {
    client_id: string;
    changes?: { [key: string]: any };
    confirmed_bookings?: IClientBooking[];
    held_bookings?: IClientBooking[];
    last_updated_by: string;
}) {
    const clientChanges = { ...changes };
    delete clientChanges.tags;
    delete clientChanges.active_tag_keys;
    if (typeof clientChanges.email === "string") {
        clientChanges.email = normalizeEmail(clientChanges.email);
    }
    if (confirmed_bookings) {
        clientChanges["confirmed_bookings"] = confirmed_bookings;
    }
    if (held_bookings) {
        clientChanges["held_bookings"] = held_bookings;
    }
    if (Object.keys(clientChanges).length < 1) {
        return;
    }
    await db
        .collection(Collection.CLIENTS)
        .doc(client_id)
        .update({
            ...clientChanges,
            last_updated_by,
            updated_at: new Date(),
            date_updated: new Date(),
            updated_from: UpdatedFrom.INTAKE,
        });
}

const SESSION_ATTENDEES_HISTORY_CAP = 25;

export async function addSessionAttendeesHistoryEntry(
    clientId: string,
    entry: ISessionAttendeesHistory
): Promise<void> {
    const clientRef = doc(dbv9, Collection.CLIENTS, clientId);
    await runTransaction(dbv9, async (transaction) => {
        const snapshot = await transaction.get(clientRef);
        if (!snapshot.exists()) return;
        const existing: ISessionAttendeesHistory[] =
            snapshot.data()?.session_attendees_history || [];
        const next = [...existing, entry].slice(-SESSION_ATTENDEES_HISTORY_CAP);
        transaction.update(clientRef, { session_attendees_history: next });
    });
}
